import { useEffect, useRef, useState, type FormEvent, type MutableRefObject } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import type { Band } from './BandCard';
import { personOptions, SearchSelect } from '../../shared/ui/SearchSelect';
import type { BibleTranslation } from '../../shared/api/types';

type InCharge = 'ministerio' | 'miembro' | 'no_aplica';
type Ref = { personId: string; name: string };
type OrderItem = {
  segmentId: string;
  name: string;
  inCharge: InCharge;
  variable: boolean;
  sameAsWelcomeOption: boolean;
  sameAsWelcome: boolean;
  ministry: { id: string; name: string; systemKey?: string | null } | null;
  person: Ref | null;
  isWelcome: boolean;
  link: SegmentLink | null;
  canEdit: boolean;
  choices: Ref[];
  /** Pasajes de la Biblia de esta parte (la consola los proyecta). */
  passages?: { id: string; passage: string; translation: string | null }[];
  canEditPassages?: boolean;
};
type Announcement = { id: string; body: string; ministry: string | null; by: string; canRemove: boolean };
type ServiceTestimony = { id: string; kind: 'testimonio' | 'gratitud'; title: string | null; body: string; status: 'pendiente' | 'aprobado' | 'rechazado'; name: string; mine: boolean };
type ServiceOrder = {
  canEdit: boolean;
  items: OrderItem[];
  ministries: { id: string; name: string }[];
  announcements: Announcement[];
  /** Ministerios desde los que quien consulta puede agendar anuncios. */
  announceAs: { id: string; name: string }[];
  canAnnounce: boolean;
  testimonies: ServiceTestimony[];
  testimonyApproval: boolean;
  canApprove: boolean;
};

type Segment = { key: string; id: string | null; name: string; inCharge: InCharge; departmentId: string | null; personId: string | null; variable: boolean; sameAsWelcome: boolean; link: SegmentLink | null };

/** Partes especiales del orden: qué hace cada una (se explica al armar el orden). */
export type SegmentLink = 'predicacion' | 'dirige' | 'despedida' | 'anuncios' | 'testimonios';
const LINKS: { link: SegmentLink; name: string; text: string; inCharge: InCharge; variable: boolean }[] = [
  { link: 'dirige', name: 'Dirige', text: 'Quien dirige el culto (se elige en cada culto). También aprueba los testimonios y da la despedida.', inCharge: 'miembro', variable: true },
  { link: 'testimonios', name: 'Testimonios y agradecimientos', text: 'Los que pide la iglesia para el culto (se aprueban antes, si así lo pides).', inCharge: 'no_aplica', variable: false },
  { link: 'predicacion', name: 'Predicación', text: 'Toma a quien predica en el programa de cada culto.', inCharge: 'miembro', variable: true },
  { link: 'anuncios', name: 'Anuncios', text: 'Quien da los anuncios (se elige en cada culto). Los líderes de ministerio agendan aquí sus anuncios.', inCharge: 'miembro', variable: true },
  { link: 'despedida', name: 'Despedida', text: 'La da quien dirige el culto.', inCharge: 'miembro', variable: false },
];
const linkText = (link: SegmentLink) => LINKS.find((l) => l.link === link)!.text;
type Template = {
  canEdit: boolean;
  typeId: string | null;
  /** El tipo de culto no tiene orden propio: usa el general (que viene en `segments`). */
  inherits: boolean;
  types: { id: string; name: string }[];
  segments: (Omit<Segment, 'key'> & { departmentName: string | null; personName: string | null })[];
  ministries: { id: string; name: string }[];
  people: Ref[];
};

export const IN_CHARGE: Record<InCharge, string> = { ministerio: 'Ministerio', miembro: 'Miembro', no_aplica: 'No aplica' };

/** Orden del culto: cada parte con quién la tiene a cargo. Lo variable se llena en cada culto. */
export function OrderCard({ churchId, serviceId, band }: { churchId: string; serviceId: string; band?: Band | undefined }) {
  const url = `/worship/churches/${churchId}/services/${serviceId}/order`;
  const data = useAsync((signal) => api.get<ServiceOrder>(url, signal), [url]);
  const [editing, setEditing] = useState<string | null>(null);
  if (data.status === 'loading') return null;
  if (data.status === 'error') return <ErrorState error={data.error} onRetry={data.reload} />;
  const o = data.data;
  return (
    <Card title="Orden del culto" action={o.canEdit ? <Link to="/app/cultos/orden">Cambiar el orden de todos los cultos</Link> : null}>
      {o.items.length === 0 ? <p className="muted">Esta iglesia todavía no tiene orden del culto.</p> : null}
      <ol className="list">
        {o.items.map((i) =>
          i.ministry?.systemKey === 'alabanza' ? (
            <AlabanzaPart key={i.segmentId} item={i} serviceId={serviceId} band={band} />
          ) : (
          <li key={i.segmentId} className="list__item service">
            <div className="service__main">
              <strong>{i.name}</strong>
              <span className="small muted">
                {i.link === 'testimonios' ? null : i.inCharge === 'no_aplica' ? 'No aplica' : null}
                {i.ministry ? `A cargo de ${i.ministry.name}` : i.inCharge === 'ministerio' ? 'Ministerio por definir' : null}
                {i.link === 'despedida' ? (i.person ? `La da quien dirige: ${i.person.name}` : 'La da quien dirige (por definir)') : null}
                {i.link !== 'despedida' && i.inCharge !== 'no_aplica' && i.person ? `${i.ministry ? ' · lo da ' : ''}${i.person.name}` : null}
                {i.link !== 'despedida' && i.inCharge === 'miembro' && !i.person ? (i.link === 'predicacion' ? 'Por definir en Predicación' : 'Por definir') : null}
                {i.sameAsWelcome ? ' (el mismo de la Bienvenida)' : null}
              </span>
              {i.link === 'anuncios' ? <Announcements url={url} order={o} onChange={data.reload} /> : null}
              {i.link === 'testimonios' ? <Testimonies url={url} order={o} onChange={data.reload} /> : null}
              {(i.passages?.length ?? 0) > 0 || i.canEditPassages ? <Passages url={`${url}/${i.segmentId}/passages`} item={i} onChange={data.reload} /> : null}
              {editing === i.segmentId ? (
                <EntryForm
                  url={`${url}/${i.segmentId}`}
                  item={i}
                  ministries={o.ministries}
                  onDone={(ok) => {
                    setEditing(null);
                    if (ok) data.reload();
                  }}
                />
              ) : null}
            </div>
            {i.canEdit && editing !== i.segmentId ? (
              <Button type="button" variant="ghost" onClick={() => setEditing(i.segmentId)}>
                {i.inCharge === 'ministerio' ? 'Quién lo da' : 'Elegir'}
              </Button>
            ) : null}
          </li>
          ),
        )}
      </ol>
    </Card>
  );
}

/** Los anuncios que agendaron los ministerios; los líderes (y la administración) agregan los suyos. */
function Announcements({ url, order: o, onChange }: { url: string; order: ServiceOrder; onChange: () => void }) {
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      setAdding(false);
      onChange();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void run(() => api.post(url.replace(/\/order$/, '/announcements'), { body: String(f.get('body') ?? '').trim(), departmentId: String(f.get('departmentId') ?? '') || null }));
  }
  return (
    <div className="order-extra">
      {o.announcements.length ? (
        <ul className="order-extra__list">
          {o.announcements.map((a) => (
            <li key={a.id}>
              <span className="pre-line">{a.body}</span>
              <span className="small muted"> · {a.ministry ?? a.by}</span>
              {a.canRemove ? (
                <button type="button" className="link-button" aria-label="Quitar anuncio" disabled={busy} onClick={() => void run(() => api.delete(`${url.replace(/\/order$/, '/announcements')}/${a.id}`))}>
                  {' '}×
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <span className="small muted">Sin anuncios agendados.</span>
      )}
      {error ? <Alert>{error}</Alert> : null}
      {o.canAnnounce && !adding ? (
        <Button type="button" variant="ghost" onClick={() => setAdding(true)}>
          + Agendar anuncio
        </Button>
      ) : null}
      {adding ? (
        <form className="form" onSubmit={add}>
          {o.announceAs.length ? (
            <div className="field">
              <label htmlFor="ann-ministry">De parte de</label>
              <select id="ann-ministry" name="departmentId" defaultValue={o.announceAs.length === 1 ? o.announceAs[0]!.id : ''}>
                {o.announceAs.length > 1 ? <option value="">La iglesia</option> : null}
                {o.announceAs.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="field">
            <label htmlFor="ann-body">Anuncio</label>
            <textarea id="ann-body" name="body" rows={2} maxLength={500} required placeholder="Ej. El sábado hay limpieza del templo a las 9:00." />
          </div>
          <div className="actions">
            <Button type="submit" busy={busy}>
              Agendar
            </Button>
            <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}

const KIND_LABEL = { testimonio: 'Testimonio', gratitud: 'Agradecimiento' } as const;

/** Testimonios y agradecimientos del culto: cualquiera pide participar; quien dirige (o la administración) aprueba. */
function Testimonies({ url, order: o, onChange }: { url: string; order: ServiceOrder; onChange: () => void }) {
  const base = url.replace(/\/order$/, '/testimonies');
  const [asking, setAsking] = useState(false);
  const [kind, setKind] = useState<'gratitud' | 'testimonio'>('gratitud');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  async function run(action: () => Promise<unknown>, success: string) {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      setAsking(false);
      setMsg({ tone: 'success', text: success });
      onChange();
    } catch (err) {
      setMsg({ tone: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }
  function ask(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void run(
      () => api.post(base, { kind, title: String(f.get('title') ?? '').trim() || null, body: String(f.get('body') ?? '').trim() }),
      o.testimonyApproval ? 'Listo. Quien dirige el culto lo revisará; también quedó en Testimonios.' : 'Listo. Ya está en el orden del culto y en Testimonios.',
    );
  }
  return (
    <div className="order-extra">
      {o.testimonies.length ? (
        <ul className="order-extra__list">
          {o.testimonies.map((t) => (
            <li key={t.id}>
              <strong>{t.name}</strong>
              <span className="small muted">
                {' '}
                · {KIND_LABEL[t.kind]}
                {t.title ? `: ${t.title}` : ''}
              </span>
              {t.status === 'pendiente' ? <span className="badge badge--warn"> Por aprobar</span> : null}
              {t.status === 'rechazado' ? <span className="badge"> No entró al orden</span> : null}
              {o.canApprove && t.status === 'pendiente' ? (
                <span className="actions actions--inline">
                  <Button type="button" variant="secondary" busy={busy} onClick={() => void run(() => api.put(`${base}/${t.id}`, { status: 'aprobado' }), `${KIND_LABEL[t.kind]} de ${t.name} aprobado.`)}>
                    Aprobar
                  </Button>
                  <Button type="button" variant="ghost" disabled={busy} onClick={() => void run(() => api.put(`${base}/${t.id}`, { status: 'rechazado' }), 'Listo, no entra al orden.')}>
                    No esta vez
                  </Button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <span className="small muted">Nadie lo ha pedido todavía.</span>
      )}
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {!asking ? (
        <Button type="button" variant="ghost" onClick={() => setAsking(true)}>
          + Pedir dar un testimonio o agradecimiento
        </Button>
      ) : (
        <form className="form" onSubmit={ask}>
          <div className="actions" role="radiogroup" aria-label="Qué quieres compartir">
            <label className="check">
              <input type="radio" checked={kind === 'gratitud'} onChange={() => setKind('gratitud')} /> Agradecimiento
            </label>
            <label className="check">
              <input type="radio" checked={kind === 'testimonio'} onChange={() => setKind('testimonio')} /> Testimonio
            </label>
          </div>
          {kind === 'testimonio' ? <Field label="Título (opcional)" name="title" maxLength={120} placeholder="Ej. Dios sanó a mi hijo" /> : null}
          <div className="field">
            <label htmlFor="tes-body">{kind === 'gratitud' ? '¿Por qué das gracias?' : 'Cuéntalo brevemente'}</label>
            <textarea id="tes-body" name="body" rows={3} maxLength={kind === 'gratitud' ? 500 : 4000} required />
          </div>
          <p className="small muted">{o.testimonyApproval ? 'Quien dirige el culto lo aprueba antes de que entre al orden. ' : ''}También queda en Testimonios.</p>
          <div className="actions">
            <Button type="submit" busy={busy}>
              Enviar
            </Button>
            <Button type="button" variant="ghost" onClick={() => setAsking(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

/**
 * Pasajes de la Biblia de una parte del culto: el líder de su ministerio (o la administración) los
 * agrega con su versión; al preparar o abrir el culto en la consola de proyección entran a Programa.
 */
function Passages({ url, item: i, onChange }: { url: string; item: OrderItem; onChange: () => void }) {
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const translations = useAsync((signal) => (adding ? api.get<{ items: BibleTranslation[] }>('/bible/translations', signal).then((r) => r.items.filter((t) => t.language === 'es')) : Promise.resolve([] as BibleTranslation[])), [adding]);
  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChange();
      return true;
    } catch (e) {
      setError(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  }
  function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const passage = String(data.get('passage') ?? '').trim();
    if (!passage) return;
    void run(() => api.post(url, { passage, translation: String(data.get('translation') ?? '') || null })).then((ok) => ok && form.reset());
  }
  const list = i.passages ?? [];
  return (
    <div className="order-passages">
      {list.length > 0 ? (
        <ul className="order-passages__list" aria-label={`Pasajes de ${i.name}`}>
          {list.map((p) => (
            <li key={p.id}>
              📖 {p.passage}
              {p.translation ? <span className="muted"> ({p.translation})</span> : null}
              {i.canEditPassages ? (
                <button type="button" className="link-button" disabled={busy} aria-label={`Quitar ${p.passage}`} onClick={() => void run(() => api.delete(`${url}/${p.id}`))}>
                  Quitar
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
      {i.canEditPassages && !adding ? (
        <button type="button" className="link-button" onClick={() => setAdding(true)}>
          + Pasaje de la Biblia
        </button>
      ) : null}
      {i.canEditPassages && adding ? (
        <form className="order-passages__form" onSubmit={add}>
          <label className="visually-hidden" htmlFor={`pas-${i.segmentId}`}>
            Cita
          </label>
          <input id={`pas-${i.segmentId}`} name="passage" placeholder="Salmos 23:1-6" maxLength={80} autoComplete="off" required />
          <label className="visually-hidden" htmlFor={`ver-${i.segmentId}`}>
            Versión
          </label>
          <select id={`ver-${i.segmentId}`} name="translation" defaultValue="">
            <option value="">Versión de siempre</option>
            {(translations.status === 'ready' ? translations.data : []).map((t) => (
              <option key={t.code} value={t.code}>
                {t.code} · {t.name}
              </option>
            ))}
          </select>
          <Button type="submit" variant="secondary" busy={busy}>
            Agregar
          </Button>
          <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
            Listo
          </Button>
        </form>
      ) : null}
    </div>
  );
}

/** Alabanza en el orden: no se elige quién la da; se ve el estado de su equipo (se arma en la pestaña Alabanza). */
function AlabanzaPart({ item: i, serviceId, band }: { item: OrderItem; serviceId: string; band?: Band | undefined }) {
  const positions = band?.positions ?? [];
  const missing = positions.filter((p) => p.people.length === 0).length;
  const status = positions.length === 0 ? null : missing === 0 ? 'Equipo completo' : missing === 1 ? 'falta 1 puesto' : `faltan ${missing} puestos`;
  return (
    <li className="list__item service">
      <div className="service__main">
        <strong>{i.name}</strong>
        <span className="small muted">
          A cargo de {i.ministry?.name ?? 'Alabanza'}
          {status ? ` · ${status}` : null}
        </span>
      </div>
      <Link className="btn btn--ghost" to={`/app/cultos/${serviceId}?ver=alabanza`} replace>
        Ver Alabanza
      </Link>
    </li>
  );
}

function EntryForm({ url, item: i, ministries, onDone }: { url: string; item: OrderItem; ministries: { id: string; name: string }[]; onDone: (ok: boolean) => void }) {
  const [same, setSame] = useState(i.sameAsWelcome);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pickMinistry = i.inCharge === 'ministerio' && i.variable && ministries.length > 0;
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.put(url, {
        departmentId: pickMinistry ? String(f.get('departmentId') ?? '') || null : (i.ministry?.id ?? null),
        personId: same ? null : String(f.get('personId') ?? '') || null,
        sameAsWelcome: same,
      });
      onDone(true);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }
  return (
    <form className="form" onSubmit={save}>
      {error ? <Alert>{error}</Alert> : null}
      {pickMinistry ? (
        <div className="field">
          <label htmlFor={`m-${i.segmentId}`}>Ministerio a cargo en este culto</label>
          <select id={`m-${i.segmentId}`} name="departmentId" defaultValue={i.ministry?.id ?? ''}>
            <option value="">Por definir</option>
            {ministries.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      {i.sameAsWelcomeOption ? (
        <label className="check">
          <input type="checkbox" checked={same} onChange={(e) => setSame(e.target.checked)} /> El mismo que da la Bienvenida
        </label>
      ) : null}
      {!same ? (
        <div className="field">
          <label htmlFor={`p-${i.segmentId}`}>{i.inCharge === 'ministerio' ? 'Quién lo da (de su equipo)' : 'Quién'}</label>
          <SearchSelect id={`p-${i.segmentId}`} name="personId" defaultValue={i.sameAsWelcome ? '' : (i.person?.personId ?? '')} emptyLabel="Por definir" options={personOptions(i.choices)} />
        </div>
      ) : null}
      <div className="actions">
        <Button type="submit" busy={busy}>
          Guardar
        </Button>
        <Button type="button" variant="ghost" onClick={() => onDone(false)}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

let nextKey = 0;
const newKey = () => `n${++nextKey}`;

/** La administración arma el orden del culto de su iglesia. */
export function OrderTemplatePage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const [typeId, setTypeId] = useState('');
  const url = `/worship/churches/${churchId}/order${typeId ? `?typeId=${typeId}` : ''}`;
  const data = useAsync((signal) => (churchId ? api.get<Template>(url, signal) : Promise.resolve(null)), [url]);
  const [version, setVersion] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  /** El editor avisa aquí si hay cambios sin guardar (para no perderlos al cambiar de cultos). */
  const dirty = useRef(false);
  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  return (
    <div className="page page--narrow">
      <p className="small">
        <Link to="/app/cultos">← Cultos</Link>
      </p>
      <header className="page__header">
        <h1>Orden del culto</h1>
        <p className="muted">Las partes del culto de tu iglesia, en orden. Lo variable se elige en cada culto.</p>
      </header>
      {notice ? <Alert tone="success">{notice}</Alert> : null}
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' && data.data && data.data.types.length ? (
        <div className="field">
          <label htmlFor="order-type">¿De qué cultos?</label>
          <select
            id="order-type"
            value={typeId}
            onChange={(e) => {
              if (dirty.current && !window.confirm('Tienes cambios sin guardar en este orden. ¿Cambiar de cultos y perderlos?')) return;
              setNotice(null);
              setTypeId(e.target.value);
            }}
          >
            <option value="">Orden general (todos los cultos)</option>
            {data.data.types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      {data.status === 'ready' && data.data ? (
        <TemplateEditor
          key={`${typeId}:${version}`}
          url={url}
          template={data.data}
          typeName={data.data.types.find((t) => t.id === typeId)?.name ?? null}
          dirtyRef={dirty}
          onSaved={() => {
            setNotice('Orden guardado. Ya aparece en cada culto.');
            setVersion((v) => v + 1);
            data.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function TemplateEditor({ url, template: t, typeName, dirtyRef, onSaved }: { url: string; template: Template; typeName: string | null; dirtyRef: MutableRefObject<boolean>; onSaved: () => void }) {
  const [rows, setRows] = useState<Segment[]>(() =>
    t.segments.map((s) => ({ key: s.id ?? newKey(), id: s.id, name: s.name, inCharge: s.inCharge, departmentId: s.departmentId, personId: s.personId, variable: s.variable, sameAsWelcome: s.sameAsWelcome, link: s.link })),
  );
  const [saved] = useState(() => JSON.stringify(rows));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const dirty = t.canEdit && JSON.stringify(rows) !== saved;
  useEffect(() => {
    dirtyRef.current = dirty;
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => {
      dirtyRef.current = false;
      window.removeEventListener('beforeunload', warn);
    };
  }, [dirty, dirtyRef]);
  if (!t.canEdit) {
    return (
      <Card title="Partes">
        <ol className="list">
          {t.segments.map((s) => (
            <li key={s.id} className="list__item">
              <strong>{s.name}</strong>
              <span className="small muted">
                {IN_CHARGE[s.inCharge]}
                {s.departmentName ? `: ${s.departmentName}` : ''}
                {s.personName ? `: ${s.personName}` : ''} · {s.link ? linkText(s.link) : s.variable ? 'Se elige en cada culto' : 'Siempre igual'}
              </span>
            </li>
          ))}
        </ol>
      </Card>
    );
  }
  const set = (key: string, patch: Partial<Segment>) => setRows((l) => l.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const move = (index: number, by: number) =>
    setRows((l) => {
      const to = index + by;
      if (to < 0 || to >= l.length) return l;
      const copy = [...l];
      [copy[index], copy[to]] = [copy[to]!, copy[index]!];
      return copy;
    });
  async function save(segments: Omit<Segment, 'key'>[]) {
    setBusy(true);
    setMsg(null);
    try {
      await api.put(url, { segments });
      onSaved();
    } catch (err) {
      setMsg({ tone: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card title={typeName ? `Partes de los cultos de ${typeName}` : 'Partes'}>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {typeName && t.inherits ? (
        <Alert tone="info">Los cultos de {typeName} usan el orden general. Ajusta estas partes y guarda para que tengan su propio orden.</Alert>
      ) : null}
      {typeName && !t.inherits ? (
        <p className="small">
          Los cultos de {typeName} tienen su propio orden.{' '}
          <button type="button" className="link-button" disabled={busy} onClick={() => {
              if (window.confirm(`¿Volver al orden general? Se borrará el orden propio de los cultos de ${typeName} y usarán el general.`)) void save([]);
            }}>
            Volver al orden general
          </button>
        </p>
      ) : null}
      <ol className="list order-editor">
        {rows.map((r, index) => (
          <li key={r.key} className="list__item">
            <div className="form">
              <div className="form__row form__row--3">
                <div className="field">
                  <label htmlFor={`name-${r.key}`}>Nombre</label>
                  <input id={`name-${r.key}`} value={r.name} maxLength={80} onChange={(e) => set(r.key, { name: e.target.value })} placeholder="Ej. Bienvenida" />
                </div>
                {r.link ? (
                  <p className="small muted">{linkText(r.link)}</p>
                ) : (
                <div className="field">
                  <label htmlFor={`charge-${r.key}`}>A cargo de</label>
                  <select id={`charge-${r.key}`} value={r.inCharge} onChange={(e) => set(r.key, { inCharge: e.target.value as InCharge })}>
                    <option value="ministerio">Ministerio</option>
                    <option value="miembro">Miembro</option>
                    <option value="no_aplica">No aplica</option>
                  </select>
                </div>
                )}
                {r.inCharge !== 'no_aplica' && !r.link ? (
                  <div className="field">
                    <label htmlFor={`var-${r.key}`}>¿Cambia en cada culto?</label>
                    <select id={`var-${r.key}`} value={r.variable ? 'variable' : 'constante'} onChange={(e) => set(r.key, { variable: e.target.value === 'variable' })}>
                      <option value="constante">Siempre igual</option>
                      <option value="variable">Se elige en cada culto</option>
                    </select>
                  </div>
                ) : null}
              </div>
              {r.inCharge === 'ministerio' && !r.link ? (
                <div className="field">
                  <label htmlFor={`dep-${r.key}`}>{r.variable ? 'Ministerio de siempre (opcional)' : 'Ministerio'}</label>
                  <select id={`dep-${r.key}`} value={r.departmentId ?? ''} onChange={(e) => set(r.key, { departmentId: e.target.value || null })}>
                    <option value="">{r.variable ? 'Se elige en cada culto' : 'Elige…'}</option>
                    {t.ministries.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              {r.inCharge === 'miembro' && !r.variable && !r.link ? (
                <div className="field">
                  <label htmlFor={`per-${r.key}`}>Miembro</label>
                  <SearchSelect id={`per-${r.key}`} value={r.personId ?? ''} onChange={(v) => set(r.key, { personId: v || null })} emptyLabel="Por definir" options={personOptions(t.people)} />
                </div>
              ) : null}
              <div className="actions actions--wrap">
                <Button type="button" variant="ghost" onClick={() => move(index, -1)} disabled={index === 0}>
                  ↑ Subir
                </Button>
                <Button type="button" variant="ghost" onClick={() => move(index, 1)} disabled={index === rows.length - 1}>
                  ↓ Bajar
                </Button>
                <Button type="button" variant="ghost" onClick={() => setRows((l) => l.filter((x) => x.key !== r.key))}>
                  Quitar
                </Button>
              </div>
            </div>
          </li>
        ))}
      </ol>
      <div className="actions actions--wrap">
        <Button type="button" variant="secondary" onClick={() => setRows((l) => [...l, { key: newKey(), id: null, name: '', inCharge: 'miembro', departmentId: null, personId: null, variable: true, sameAsWelcome: false, link: null }])}>
          Agregar parte
        </Button>
        {LINKS.filter((l) => !rows.some((r) => r.link === l.link)).map((l) => (
          <Button key={l.link} type="button" variant="secondary" onClick={() => setRows((rs) => [...rs, { key: newKey(), id: null, name: l.name, inCharge: l.inCharge, departmentId: null, personId: null, variable: l.variable, sameAsWelcome: false, link: l.link }])}>
            Agregar {l.name}
          </Button>
        ))}
        <Button type="button" busy={busy} onClick={() => void save(rows.map(({ key: _key, ...r }) => r))}>
          Guardar orden
        </Button>
      </div>
    </Card>
  );
}
